import { Info, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

type Tone = 'blue' | 'orange'

const TONES: Record<Tone, string> = {
  blue: 'border-primary/15 bg-primary/10 text-primary dark:bg-primary/15',
  orange: 'border-brand-orange/20 bg-brand-orange/12 text-brand-orange',
}

type PanelProps = {
  icon: LucideIcon
  title: ReactNode
  hint?: ReactNode
  tone?: Tone
  actions?: ReactNode
  className?: string
  bodyClassName?: string
  children: ReactNode
}

export function Panel({ icon: Icon, title, hint, tone = 'blue', actions, className, bodyClassName, children }: PanelProps) {
  return (
    <section className={cn('overflow-hidden rounded-2xl border bg-card shadow-xs', className)}>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/40 px-4 py-3.5 sm:px-5 dark:bg-muted/20">
        <div className="flex min-w-0 items-center gap-3">
          <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg border sm:size-10 sm:rounded-xl', TONES[tone])}>
            <Icon className="size-4 sm:size-5" />
          </span>
          <div className="min-w-0">
            <h3 className="font-semibold leading-tight">{title}</h3>
            {hint ? <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p> : null}
          </div>
        </div>
        {actions}
      </header>
      <div className={cn('p-4 sm:p-5', bodyClassName)}>{children}</div>
    </section>
  )
}

export function InfoNote({ title, children, className }: { title?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-start gap-3 rounded-2xl border border-primary/15 bg-primary/[0.04] p-4 text-sm dark:bg-primary/10', className)}>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-card text-primary">
        <Info className="size-4" />
      </span>
      <div className="min-w-0 space-y-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        <div className="text-[13px] leading-relaxed text-muted-foreground">{children}</div>
      </div>
    </div>
  )
}
